/**
 * Red Light Rising — Dosage Calculator Engine
 * File: assets/dosage-calculator.js
 * Version: 1.1
 * Last updated: 17 April 2026
 * 
 * PURPOSE: All calculation logic, interpolation, auto-solve, input validation,
 * debounce, time conversion, URL encoding, and GA4 event tracking.
 * 
 * DEPENDENCIES:
 * - window.RLR_DEVICES (from dosage-data.liquid)
 * - window.RLR_PANEL_DECAY (from dosage-data.liquid)
 * - window.RLR_HANDHELD_DECAY (from dosage-data.liquid)
 * - window.RLR_DISTANCES (from dosage-data.liquid)
 * - html2canvas (from assets/html2canvas.min.js) — only for protocol card
 * - jsPDF (from assets/jspdf.umd.min.js) — only for protocol card
 * 
 * SECURITY:
 * - No eval() or new Function() calls
 * - No innerHTML assignments with user data
 * - All user inputs sanitised via parseFloat + isFinite checks
 * - URL parameters validated against known keys only
 * - All DOM updates use textContent, not innerHTML
 */

(function () {
  'use strict';

  // ============================================================
  // SECTION 1: UTILITY FUNCTIONS
  // ============================================================

  /**
   * Sanitise a numeric input. Returns null if invalid.
   */
  function sanitiseNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    var num = parseFloat(value);
    if (!isFinite(num) || num < 0) return null;
    return num;
  }

  /**
   * Convert minutes + seconds inputs to total seconds.
   */
  function getTotalSeconds(minutes, seconds) {
    var m = parseInt(minutes, 10) || 0;
    var s = parseInt(seconds, 10) || 0;
    return (m * 60) + s;
  }

  /**
   * Format total seconds into a friendly string.
   * e.g. 432 => "7 min 12 sec"
   */
  function formatTime(totalSeconds) {
    if (totalSeconds === null || totalSeconds === undefined) return '';
    var m = Math.floor(totalSeconds / 60);
    var s = Math.round(totalSeconds % 60);
    if (s === 60) { m += 1; s = 0; } // handle rounding edge case
    if (m === 0) return s + ' sec';
    if (s === 0) return m + ' min';
    return m + ' min ' + s + ' sec';
  }

  /**
   * Format minutes and seconds from total seconds (for input fields).
   */
  function splitTime(totalSeconds) {
    if (totalSeconds === null || totalSeconds === undefined) return { minutes: '', seconds: '' };
    var m = Math.floor(totalSeconds / 60);
    var s = Math.round(totalSeconds % 60);
    if (s === 60) { m += 1; s = 0; }
    return { minutes: m, seconds: s };
  }

  /**
   * Round a number to a given number of decimal places.
   */
  function roundTo(num, decimals) {
    var factor = Math.pow(10, decimals);
    return Math.round(num * factor) / factor;
  }

  /**
   * Debounce function. Delays execution until the user stops typing.
   */
  function debounce(fn, delay) {
    var timer;
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, delay);
    };
  }


  // ============================================================
  // SECTION 2: POWER-LAW INTERPOLATION
  // ============================================================

  /**
   * Power-law interpolation between two known data points.
   * 
   * Light decay roughly follows I = a * d^k where:
   * - I = irradiance
   * - d = distance
   * - k = decay exponent (derived from two known points)
   * - a = scaling constant
   * 
   * This is more physically accurate than linear interpolation,
   * especially for the steep part of the handheld decay curve.
   * 
   * Edge case: if either distance is 0, fall back to linear interpolation
   * (power-law is undefined at d=0 because log(0) is -infinity).
   */
  function powerInterpolate(d1, v1, d2, v2, targetD) {
    // Safety: if distances are identical, return the value
    if (d1 === d2) return v1;

    // Edge case: power-law fails at d=0. Use linear interpolation instead.
    if (d1 === 0 || d2 === 0 || targetD === 0) {
      var linearFraction = (targetD - d1) / (d2 - d1);
      return v1 + linearFraction * (v2 - v1);
    }

    // Edge case: if either value is 0 or negative, use linear
    if (v1 <= 0 || v2 <= 0) {
      var frac = (targetD - d1) / (d2 - d1);
      return v1 + frac * (v2 - v1);
    }

    // Power-law: k = ln(v2/v1) / ln(d1/d2)
    var k = Math.log(v2 / v1) / Math.log(d1 / d2);
    var a = v1 * Math.pow(d1, -k);
    return a * Math.pow(targetD, k);
  }

  /**
   * Interpolate within a decay table (array of {distance, factor} objects).
   * Finds the two bracketing entries and uses power-law interpolation.
   * 
   * If the target distance is outside the table range:
   * - Below minimum: clamp to minimum factor
   * - Above maximum: extrapolate from the last two points (with caution)
   */
  function interpolateFromTable(table, targetDistance) {
    if (!table || table.length === 0) return 1.0;

    // Exact match check
    for (var i = 0; i < table.length; i++) {
      if (table[i].distance === targetDistance) return table[i].factor;
    }

    // Below range: clamp to first entry
    if (targetDistance <= table[0].distance) return table[0].factor;

    // Above range: extrapolate from last two entries
    if (targetDistance >= table[table.length - 1].distance) {
      var last = table[table.length - 1];
      var prev = table[table.length - 2];
      return powerInterpolate(prev.distance, prev.factor, last.distance, last.factor, targetDistance);
    }

    // Find bracketing pair
    for (var j = 0; j < table.length - 1; j++) {
      if (table[j].distance <= targetDistance && table[j + 1].distance >= targetDistance) {
        return powerInterpolate(
          table[j].distance, table[j].factor,
          table[j + 1].distance, table[j + 1].factor,
          targetDistance
        );
      }
    }

    // Fallback (should never reach here)
    return 1.0;
  }

  /**
   * Reverse lookup: given a target irradiance, find the distance.
   * Walks the measured data or decay table to find the distance where
   * irradiance equals the target. Uses power-law interpolation between points.
   * 
   * Returns distance in cm, or null if outside realistic range.
   */
  function findDistanceForIrradiance(device, targetIrradiance) {
    // Build a distance-to-irradiance table for this device
    var points = buildIrradianceTable(device);
    if (points.length < 2) return null;

    // Sort by distance ascending
    points.sort(function (a, b) { return a.distance - b.distance; });

    // Check if target is outside our range
    if (targetIrradiance >= points[0].irradiance) {
      // Target is higher than our closest reading — too close
      return null;
    }
    if (targetIrradiance <= points[points.length - 1].irradiance) {
      // Target is lower than our furthest reading — too far
      if (targetIrradiance <= 0) return null;
      // Extrapolate
      var last = points[points.length - 1];
      var prev = points[points.length - 2];
      var estDistance = powerInterpolate(prev.irradiance, prev.distance, last.irradiance, last.distance, targetIrradiance);
      if (estDistance > 100) return null; // Beyond realistic range
      return roundTo(estDistance, 1);
    }

    // Find the bracket where target falls
    for (var i = 0; i < points.length - 1; i++) {
      var high = points[i].irradiance;
      var low = points[i + 1].irradiance;
      if (targetIrradiance <= high && targetIrradiance >= low) {
        var dist = powerInterpolate(high, points[i].distance, low, points[i + 1].distance, targetIrradiance);
        return roundTo(dist, 1);
      }
    }

    return null;
  }

  /**
   * Build a full distance-to-irradiance table for a device.
   * Combines measured values with decay model estimates.
   */
  function buildIrradianceTable(device) {
    var points = [];
    var distances = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65];

    for (var i = 0; i < distances.length; i++) {
      var d = distances[i];
      var irr = getIrradiance(device, d, null);
      if (irr !== null && irr > 0) {
        points.push({ distance: d, irradiance: irr });
      }
    }

    return points;
  }


  // ============================================================
  // SECTION 3: IRRADIANCE CALCULATION (MEASUREMENT-FIRST)
  // ============================================================

  /**
   * Get irradiance for a device at a given distance.
   * 
   * PRIORITY ORDER:
   * 1. Custom user-entered irradiance (for "Enter your own" mode)
   * 2. Contact device (Wrap) — fixed at contact irradiance
   * 3. Exact measured value at the requested distance
   * 4. For devices with multi-distance data — power-law interpolation
   *    between the two nearest measured points
   * 5. Fall back to the generic decay model (panel or handheld)
   * 
   * The TL3.0 at 5cm returns 34.0 (measured), NOT 22.2 (modelled).
   * This is the critical fix from the v1.1 review.
   * 
   * @param {Object} device - Device object from RLR_DEVICES
   * @param {number} distanceCm - Distance in centimetres
   * @param {number|null} userIrradiance - User-entered irradiance (custom mode only)
   * @returns {number|null} Irradiance in mW/cm², or null if unavailable
   */
  function getIrradiance(device, distanceCm, userIrradiance) {

    // PRIORITY 1: Custom device — user provides their own irradiance
    if (device.type === 'custom') {
      if (userIrradiance !== null && userIrradiance !== undefined) {
        return sanitiseNumber(userIrradiance);
      }
      return null;
    }

    // PRIORITY 2: Contact device (Wrap) — always returns contact reading
    if (device.decayModel === 'contact') {
      return (device.measured && device.measured[0] !== undefined) ? device.measured[0] : null;
    }

    // PRIORITY 3: Exact measured value at this distance
    if (device.measured && device.measured[distanceCm] !== undefined) {
      return device.measured[distanceCm];
    }

    // Get sorted array of measured distances for this device
    var measuredDistances = [];
    if (device.measured) {
      for (var key in device.measured) {
        if (device.measured.hasOwnProperty(key)) {
          measuredDistances.push(parseInt(key, 10));
        }
      }
    }
    measuredDistances.sort(function (a, b) { return a - b; });

    // PRIORITY 4: Interpolate between measured points (for multi-distance devices)
    if (measuredDistances.length >= 2) {
      var minMeasured = measuredDistances[0];
      var maxMeasured = measuredDistances[measuredDistances.length - 1];

      if (distanceCm >= minMeasured && distanceCm <= maxMeasured) {
        // Find the two bracketing measured distances
        for (var i = 0; i < measuredDistances.length - 1; i++) {
          var dLow = measuredDistances[i];
          var dHigh = measuredDistances[i + 1];
          if (distanceCm >= dLow && distanceCm <= dHigh) {
            return roundTo(powerInterpolate(
              dLow, device.measured[dLow],
              dHigh, device.measured[dHigh],
              distanceCm
            ), 2);
          }
        }
      }
    }

    // PRIORITY 5: Fall back to the generic decay model
    // Base irradiance is always the 15cm measurement
    var baseIrradiance = device.measured ? device.measured[15] : null;
    if (!baseIrradiance) return null;

    var decayTable;
    if (device.decayModel === 'panel') {
      decayTable = window.RLR_PANEL_DECAY;
    } else {
      decayTable = window.RLR_HANDHELD_DECAY;
    }

    var factor = interpolateFromTable(decayTable, distanceCm);
    return roundTo(baseIrradiance * factor, 2);
  }


  // ============================================================
  // SECTION 4: DOSE CALCULATION
  // ============================================================

  /**
   * Calculate dose from irradiance and time.
   * Dose (J/cm²) = Irradiance (mW/cm²) × Time (seconds) / 1000
   */
  function calculateDose(irradianceMw, timeSeconds) {
    if (irradianceMw === null || timeSeconds === null) return null;
    if (irradianceMw <= 0 || timeSeconds <= 0) return null;
    return roundTo((irradianceMw * timeSeconds) / 1000, 1);
  }

  /**
   * Calculate time from irradiance and target dose.
   * Time (seconds) = Dose (J/cm²) × 1000 / Irradiance (mW/cm²)
   */
  function calculateTime(irradianceMw, doseJcm2) {
    if (irradianceMw === null || doseJcm2 === null) return null;
    if (irradianceMw <= 0 || doseJcm2 <= 0) return null;
    return Math.round((doseJcm2 * 1000) / irradianceMw);
  }

  /**
   * Calculate required irradiance from time and dose.
   * Irradiance (mW/cm²) = Dose (J/cm²) × 1000 / Time (seconds)
   */
  function calculateRequiredIrradiance(doseJcm2, timeSeconds) {
    if (doseJcm2 === null || timeSeconds === null) return null;
    if (doseJcm2 <= 0 || timeSeconds <= 0) return null;
    return roundTo((doseJcm2 * 1000) / timeSeconds, 2);
  }


  // ============================================================
  // SECTION 5: AUTO-SOLVE LOGIC (LAST-TOUCHED STATE)
  // ============================================================

  /**
   * Determine what to solve for based on available inputs and last-touched field.
   * 
   * @param {Object} state - Current calculator state:
   *   - device: device object or null
   *   - distanceCm: number or null (null = distance not selected)
   *   - timeSeconds: number or null
   *   - doseJcm2: number or null
   *   - userIrradiance: number or null (for custom mode)
   *   - lastTouched: 'time' | 'dose' | 'distance' | null
   * 
   * @returns {Object} Result:
   *   - solvedFor: 'dose' | 'time' | 'distance' | 'none'
   *   - irradiance: number or null
   *   - dose: number or null
   *   - timeSeconds: number or null
   *   - distanceCm: number or null
   *   - warnings: array of warning strings
   *   - errors: array of error strings
   */
  function solve(state) {
    var result = {
      solvedFor: 'none',
      irradiance: null,
      dose: null,
      timeSeconds: null,
      distanceCm: state.distanceCm,
      warnings: [],
      errors: []
    };

    if (!state.device) {
      result.errors.push('Please select a device.');
      return result;
    }

    // Get irradiance at selected distance
    var irradiance = null;

    if (state.device.type === 'custom') {
      // Custom mode: user enters irradiance directly, no distance needed
      irradiance = sanitiseNumber(state.userIrradiance);
      if (!irradiance) {
        result.errors.push('Please enter your device irradiance in mW/cm\u00B2.');
        return result;
      }
    } else if (state.device.decayModel === 'contact') {
      // Wrap: fixed at contact
      irradiance = getIrradiance(state.device, 0, null);
    } else if (state.distanceCm !== null) {
      // Normal mode: device + distance selected
      irradiance = getIrradiance(state.device, state.distanceCm, null);
    }

    result.irradiance = irradiance;

    // Determine what to solve for
    var hasDistance = (state.distanceCm !== null || state.device.decayModel === 'contact' || state.device.type === 'custom');
    var hasTime = (state.timeSeconds !== null && state.timeSeconds > 0);
    var hasDose = (state.doseJcm2 !== null && state.doseJcm2 > 0);

    // Case 1: No distance selected, but time + dose provided => solve for distance
    if (!hasDistance && hasTime && hasDose && state.device.type !== 'custom') {
      var requiredIrradiance = calculateRequiredIrradiance(state.doseJcm2, state.timeSeconds);
      if (requiredIrradiance !== null) {
        var foundDistance = findDistanceForIrradiance(state.device, requiredIrradiance);
        if (foundDistance !== null) {
          if (foundDistance < 0) {
            result.errors.push('The target dose cannot be achieved \u2014 the required irradiance exceeds this device\u2019s maximum output.');
          } else if (foundDistance > 100) {
            result.errors.push('The required distance exceeds 100cm. At this distance, irradiance estimates become unreliable.');
          } else {
            result.solvedFor = 'distance';
            result.distanceCm = foundDistance;
            result.irradiance = requiredIrradiance;
            result.dose = state.doseJcm2;
            result.timeSeconds = state.timeSeconds;
            result.warnings.push('Estimated distance. Actual results may vary depending on your specific device and environment.');
          }
        } else {
          result.errors.push('Could not calculate a suitable distance for this combination of time and dose.');
        }
      }
      return result;
    }

    // If we don't have irradiance at this point, we can't calculate anything else
    if (!irradiance) {
      if (!hasDistance) {
        result.errors.push('Select a distance, or enter time and dose to calculate the required distance.');
      }
      return result;
    }

    // Case 2: Irradiance known + time provided => solve for dose
    // Case 3: Irradiance known + dose provided => solve for time
    // Case 4: All three provided => use lastTouched to decide
    if (hasTime && hasDose) {
      // All values present — use lastTouched to decide
      if (state.lastTouched === 'dose') {
        // User just entered dose, solve for time
        result.solvedFor = 'time';
        result.timeSeconds = calculateTime(irradiance, state.doseJcm2);
        result.dose = state.doseJcm2;
      } else {
        // Default: solve for dose (user changed time or distance)
        result.solvedFor = 'dose';
        result.dose = calculateDose(irradiance, state.timeSeconds);
        result.timeSeconds = state.timeSeconds;
      }
    } else if (hasTime && !hasDose) {
      // Solve for dose
      result.solvedFor = 'dose';
      result.dose = calculateDose(irradiance, state.timeSeconds);
      result.timeSeconds = state.timeSeconds;
    } else if (hasDose && !hasTime) {
      // Solve for time
      result.solvedFor = 'time';
      result.timeSeconds = calculateTime(irradiance, state.doseJcm2);
      result.dose = state.doseJcm2;
    } else {
      // Only irradiance known, nothing to solve yet
      result.errors.push('Enter your session time or target dose.');
    }

    // Input validation warnings
    if (result.timeSeconds !== null && result.timeSeconds > 3600) {
      result.warnings.push('Session times over 60 minutes are unusual. Please double check your input.');
    }
    if (result.dose !== null && result.dose > 60) {
      result.warnings.push('This is a very high dose. Most red light therapy research uses doses between 4 and 60 J/cm\u00B2.');
    }
    if (state.userIrradiance !== null && state.userIrradiance > 400) {
      result.warnings.push('This is an exceptionally high irradiance. Please verify your measurement.');
    }

    return result;
  }


  // ============================================================
  // SECTION 6: DOSE CONTEXT
  // ============================================================

  /**
   * Get a context string for the calculated dose.
   * Based on common dose ranges in published research.
   */
  function getDoseContext(doseJcm2) {
    if (doseJcm2 === null) return '';

    if (doseJcm2 < 4) {
      return 'Below the range commonly studied in red light therapy research.';
    } else if (doseJcm2 <= 10) {
      return 'Within the range commonly studied for surface applications (skin, superficial tissue).';
    } else if (doseJcm2 <= 30) {
      return 'Within the range commonly studied for deeper tissue applications (muscle, joints).';
    } else if (doseJcm2 <= 60) {
      return 'At the higher end of commonly studied dose ranges.';
    } else {
      return 'Above the range used in most published red light therapy research.';
    }
  }

  /**
   * Get the position (0-100%) on the visual dose bar.
   * Maps 0-80 J/cm² to the bar width. Capped at 100%.
   */
  function getDoseBarPosition(doseJcm2) {
    if (doseJcm2 === null || doseJcm2 <= 0) return 0;
    var maxDose = 80; // visual scale cap
    return Math.min(100, (doseJcm2 / maxDose) * 100);
  }


  // ============================================================
  // SECTION 7: SHAREABLE URL
  // ============================================================

  /**
   * Encode current calculator state into URL parameters.
   */
  function encodeStateToURL(state) {
    var params = new URLSearchParams();

    if (state.deviceKey) params.set('device', state.deviceKey);
    if (state.distanceCm !== null) params.set('distance', state.distanceCm);

    if (state.timeSeconds !== null) {
      var split = splitTime(state.timeSeconds);
      params.set('minutes', split.minutes);
      params.set('seconds', split.seconds);
    }

    if (state.doseJcm2 !== null) params.set('dose', state.doseJcm2);
    if (state.userIrradiance !== null) params.set('irradiance', state.userIrradiance);

    return params.toString();
  }

  /**
   * Decode URL parameters into calculator state.
   * All values are validated before use.
   */
  function decodeStateFromURL() {
    var params = new URLSearchParams(window.location.search);
    var state = {
      deviceKey: null,
      distanceCm: null,
      minutes: null,
      seconds: null,
      doseJcm2: null,
      userIrradiance: null
    };

    // Device key: validate against known keys
    var deviceKey = params.get('device');
    if (deviceKey && window.RLR_DEVICES && window.RLR_DEVICES[deviceKey]) {
      state.deviceKey = deviceKey;
    }

    // Distance: validate as a positive number
    var distance = sanitiseNumber(params.get('distance'));
    if (distance !== null && distance >= 0 && distance <= 100) {
      state.distanceCm = distance;
    }

    // Time: validate as positive integers
    var minutes = sanitiseNumber(params.get('minutes'));
    if (minutes !== null && minutes >= 0 && minutes <= 60) {
      state.minutes = Math.floor(minutes);
    }
    var seconds = sanitiseNumber(params.get('seconds'));
    if (seconds !== null && seconds >= 0 && seconds < 60) {
      state.seconds = Math.floor(seconds);
    }

    // Dose: validate as a positive number in range
    var dose = sanitiseNumber(params.get('dose'));
    if (dose !== null && dose > 0 && dose <= 200) {
      state.doseJcm2 = dose;
    }

    // Custom irradiance: validate
    var irradiance = sanitiseNumber(params.get('irradiance'));
    if (irradiance !== null && irradiance > 0 && irradiance <= 500) {
      state.userIrradiance = irradiance;
    }

    return state;
  }

  /**
   * Update the browser URL without reloading the page.
   */
  function updateURL(state) {
    var queryString = encodeStateToURL(state);
    var newURL = window.location.pathname + (queryString ? '?' + queryString : '');
    window.history.replaceState(null, '', newURL);
  }


  // ============================================================
  // SECTION 8: GA4 EVENT TRACKING
  // ============================================================

  /**
   * Push a GA4 event to the dataLayer.
   * No personal data is collected. All parameters are anonymous.
   */
  function trackEvent(eventName, params) {
    window.dataLayer = window.dataLayer || [];
    var eventData = { event: eventName };
    if (params) {
      for (var key in params) {
        if (params.hasOwnProperty(key)) {
          eventData[key] = params[key];
        }
      }
    }
    window.dataLayer.push(eventData);
  }


  // ============================================================
  // SECTION 9: PROTOCOL CARD PDF GENERATION
  // ============================================================

  /**
   * Generate and download a branded protocol card PDF.
   * Uses html2canvas to snapshot a styled HTML template,
   * then jsPDF to create the PDF with the brand fonts intact.
   * 
   * @param {Object} data - Protocol card data:
   *   - deviceName: string
   *   - deviceKey: string
   *   - wavelengths: string
   *   - distanceCm: number
   *   - distanceFormatted: string
   *   - timeSeconds: number
   *   - timeFormatted: string
   *   - dose: number
   */
  function generateProtocolCard(data) {
    var template = document.getElementById('protocol-card-template');
    if (!template) {
      console.error('Protocol card template not found in DOM.');
      return;
    }

    // Populate the template with calculation data
    var deviceEl = template.querySelector('.protocol-device');
    var wavelengthsEl = template.querySelector('.protocol-wavelengths');
    var distanceEl = template.querySelector('.protocol-distance');
    var timeEl = template.querySelector('.protocol-time');
    var doseEl = template.querySelector('.protocol-dose');

    if (deviceEl) deviceEl.textContent = data.deviceName;
    if (wavelengthsEl) wavelengthsEl.textContent = data.wavelengths;
    if (distanceEl) distanceEl.textContent = data.distanceFormatted;
    if (timeEl) timeEl.textContent = data.timeFormatted;
    if (doseEl) doseEl.textContent = data.dose + ' J/cm\u00B2';

    // Make template visible off-screen for html2canvas
    template.style.position = 'fixed';
    template.style.left = '-9999px';
    template.style.top = '0';
    template.style.display = 'block';

    // Check html2canvas is available
    if (typeof html2canvas !== 'function') {
      console.error('html2canvas not loaded. Cannot generate protocol card.');
      template.style.display = 'none';
      return;
    }

    html2canvas(template, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#FFFFFF'
    }).then(function (canvas) {
      // Create PDF (A5 portrait: 148mm x 210mm)
      var pdf = new jspdf.jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a5'
      });

      var imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, 0, 148, 210);
      pdf.save('red-light-rising-protocol.pdf');

      // Track the download
      trackEvent('protocol_downloaded', {
        device: data.deviceKey,
        distance_cm: data.distanceCm,
        time_seconds: data.timeSeconds,
        dose_jcm2: data.dose
      });
    }).catch(function (err) {
      console.error('Failed to generate protocol card:', err);
    }).finally(function () {
      // Hide template again
      template.style.display = 'none';
    });
  }


  // ============================================================
  // SECTION 10: COPY SHARE LINK
  // ============================================================

  /**
   * Copy the current shareable URL to clipboard.
   * Shows a brief "Link copied" notification.
   */
  function copyShareLink(state) {
    var queryString = encodeStateToURL(state);
    var fullURL = window.location.origin + window.location.pathname + '?' + queryString;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(fullURL).then(function () {
        showToast('Link copied');
        trackEvent('calculation_shared', {
          device: state.deviceKey,
          distance_cm: state.distanceCm
        });
      }).catch(function () {
        fallbackCopy(fullURL);
      });
    } else {
      fallbackCopy(fullURL);
    }
  }

  function fallbackCopy(text) {
    var textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    try { document.execCommand('copy'); showToast('Link copied'); }
    catch (e) { showToast('Could not copy link'); }
    document.body.removeChild(textarea);
  }

  function showToast(message) {
    var existing = document.getElementById('rlr-toast');
    if (existing) existing.remove();

    var toast = document.createElement('div');
    toast.id = 'rlr-toast';
    toast.textContent = message;
    toast.style.cssText = 'position:fixed;bottom:20px;left:50%;transform:translateX(-50%);background:#1E1E1E;color:#F5EFE4;padding:12px 24px;border-radius:4px;font-family:Inter,sans-serif;font-size:14px;z-index:99999;opacity:0;transition:opacity 0.3s;';
    document.body.appendChild(toast);

    // Fade in
    setTimeout(function () { toast.style.opacity = '1'; }, 10);
    // Fade out and remove
    setTimeout(function () {
      toast.style.opacity = '0';
      setTimeout(function () { toast.remove(); }, 300);
    }, 2000);
  }


  // ============================================================
  // SECTION 11: PUBLIC API
  // ============================================================

  /**
   * Expose the calculator engine as a global object.
   * The Liquid section template will use these functions
   * to wire up the UI elements.
   */
  window.RLRCalculator = {
    // Core functions
    getIrradiance: getIrradiance,
    calculateDose: calculateDose,
    calculateTime: calculateTime,
    calculateRequiredIrradiance: calculateRequiredIrradiance,
    solve: solve,

    // Context
    getDoseContext: getDoseContext,
    getDoseBarPosition: getDoseBarPosition,

    // Utilities
    sanitiseNumber: sanitiseNumber,
    getTotalSeconds: getTotalSeconds,
    formatTime: formatTime,
    splitTime: splitTime,
    roundTo: roundTo,
    debounce: debounce,

    // URL
    encodeStateToURL: encodeStateToURL,
    decodeStateFromURL: decodeStateFromURL,
    updateURL: updateURL,

    // Tracking
    trackEvent: trackEvent,

    // Protocol card
    generateProtocolCard: generateProtocolCard,

    // Share
    copyShareLink: copyShareLink
  };

})();
